export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your rehabilitation resources were already organized by surgical procedure?',
    paragraphs: [
      'Not every patient recovering from knee surgery needs the same exercises. An ACL reconstruction, a meniscus repair, a knee replacement, and a tibial plateau fracture each require a different approach.',
      'In everyday practice, physical therapists encounter:',
    ],
    list: [
      'Different postoperative range-of-motion restrictions.',
      'Ligament reconstructions requiring specific progressions.',
      'Meniscal repairs with weight-bearing limitations.',
      'Tendon repairs requiring extensor mechanism protection.',
      'Repaired fractures where fixation influences rehabilitation.',
    ],
    outro: [
      'The Complete Postoperative Knee Rehabilitation System™ brings together seven essential areas of postoperative knee rehabilitation, plus five professional resource collections.',
    ],
    highlight: 'Identify. Assess. Select. Rehabilitate. Reassess.',
  },
  {
    variant: 'gold' as const,
    title: 'Spend less time organizing materials. Have more resources for your sessions.',
    paragraphs: [
      "You shouldn't have to rebuild exercise guides, search through different protocols, or prepare every resource from scratch. Open the volume for your patient's procedure and find organized materials to support treatment planning.",
    ],
    outro: [
      "Every intervention must be individualized according to the patient's clinical findings and the surgeon's instructions.",
    ],
    highlight: 'Less repetitive preparation. Better organized resources.',
  },
  {
    variant: 'navy' as const,
    title: 'Every surgery has different needs. Your resources should reflect that.',
    paragraphs: [],
    qa: [
      { q: 'ACL reconstruction?', a: 'Progressions for mobility, quadriceps strength, neuromuscular control, and function.' },
      { q: 'Meniscus surgery?', a: 'Meniscectomy, meniscal repair, and root repair resources with relevant precautions.' },
      { q: 'PCL or multiligament reconstruction?', a: 'Exercises compatible with the reconstructed structures.' },
      { q: 'Knee replacement?', a: 'Extension, flexion, strength, gait, balance, and daily activities.' },
      { q: 'Patellar stabilization or tendon repair?', a: 'Materials accounting for ligament, tendon, and osteotomy protection.' },
      { q: 'Cartilage restoration or osteotomy?', a: 'Surgical protection, authorized loading, and functional recovery.' },
      { q: 'Surgically repaired fractures?', a: 'Mobility, strength, and walking with fixation-based restrictions.' },
    ],
    highlight: 'Open the volume. Identify the procedure. Adapt the treatment.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'ACL Reconstruction & Revision Rehabilitation',
    subtitle: 'Advanced Rehabilitation After ACL Reconstruction and Revision Surgery',
    points: [
      'Protocols for primary and revision ACL reconstruction, including patellar tendon, hamstring, quadriceps tendon, and allograft considerations.',
    ],
    list: [
      'Extension and flexion restoration',
      'Quadriceps activation and strengthening',
      'Neuromuscular control',
      'Gait rehabilitation',
      'Functional assessments',
      'Preparation for higher-demand activities',
    ],
    finalLine: 'Assess. Activate. Strengthen. Progress.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '02',
    title: 'Meniscus Surgery & Repair Rehabilitation',
    subtitle: 'Procedure-Specific Rehabilitation After Meniscal Surgery',
    points: [
      'Covers partial meniscectomy, vertical, radial, horizontal and complex repairs, root repairs, and meniscal transplantation.',
    ],
    list: [
      'Mobility and loading considerations',
      'Protected range-of-motion training',
      'Strengthening progressions',
      'Gait restoration',
      'Symptom monitoring',
      'Reassessment criteria',
    ],
    tagline: 'Not every meniscal repair requires the same rehabilitation approach.',
    finalLine: 'Protect. Mobilize. Strengthen. Reassess.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '03',
    title: 'PCL & Multiligament Knee Rehabilitation',
    subtitle: 'Rehabilitation Following PCL and Complex Ligament Reconstruction',
    points: ['Resources for PCL, MCL, LCL/FCL, posterolateral corner, and combined ligament reconstructions.'],
    list: [
      'Identify reconstructed structures',
      'Review postoperative restrictions',
      'Select appropriate movements',
      'Develop progressive strength',
      'Rebuild functional stability',
      'Assess movement control',
    ],
    finalLine: 'Identify. Protect. Control. Strengthen.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '04',
    title: 'Knee Replacement Rehabilitation',
    subtitle: 'Rehabilitation After Total, Partial, and Revision Knee Arthroplasty',
    points: ['For total, unicompartmental, patellofemoral, and revision knee replacement.'],
    list: [
      'Initial postoperative assessment',
      'Extension and flexion restoration',
      'Progressive strengthening',
      'Gait and stair training',
      'Balance and functional mobility',
      'Clinical progress assessment',
    ],
    finalLine: 'Mobilize. Strengthen. Walk. Restore function.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '05',
    title: 'Patellar Stabilization & Tendon Repair Rehabilitation',
    subtitle: 'Patellar Stabilization and Extensor Mechanism Rehabilitation',
    points: ['For MPFL reconstruction, tibial tubercle osteotomy, and patellar or quadriceps tendon repair.'],
    list: [
      'Postoperative protection',
      'Authorized knee movement',
      'Progressive therapeutic exercises',
      'Extensor mechanism recovery',
      'Patellar control',
      'Strength and movement assessment',
    ],
    finalLine: 'Protect. Activate. Control. Recover.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '06',
    title: 'Cartilage Restoration & Knee Osteotomy Rehabilitation',
    subtitle: 'Rehabilitation After Cartilage Restoration and Corrective Knee Osteotomy',
    points: ['Covers microfracture, OATS, OCA, MACI/ACI, and tibial or femoral osteotomy.'],
    list: [
      'Weight-bearing and mobility restrictions',
      'Exercises during protected recovery',
      'Progressive strengthening',
      'Gait restoration',
      'Symptom and effusion monitoring',
      'Functional assessment',
    ],
    finalLine: 'Protect. Mobilize. Progress loading. Reassess.',
    meta: '40 Pages of Professional Clinical Resources',
  },
  {
    number: '07',
    title: 'Postoperative Knee Fracture Rehabilitation',
    subtitle: 'Rehabilitation Following Surgically Repaired Knee Fractures',
    points: ['For surgically fixed tibial plateau, distal femur, and patellar fractures.'],
    list: [
      'Fracture and fixation considerations',
      'Weight-bearing restrictions',
      'Authorized knee mobility',
      'Assistive device training',
      'Strength restoration',
      'Gait and functional rehabilitation',
    ],
    finalLine: 'Assess. Protect. Mobilize. Rehabilitate.',
    meta: '40 Pages of Professional Clinical Resources',
  },
];

// Pages shown in the "Take a look inside" carousel, in order 1–6.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/vohkvnbX6FUsTJUSGjUgbG/Captura_de_pantalla_2026-10-24_213736.png',
    alt: 'Volume 01 cover — ACL Reconstruction & Revision Rehabilitation.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/embbr4HHtPmABL9aPkLFjC/Captura_de_pantalla_2026-10-24_213752.png',
    alt: 'Graft-specific rehabilitation matrix — BTB, hamstring, quadriceps tendon, and allograft considerations.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/7bY5VfPLN6DVEGKmVbttse/Captura_de_pantalla_2026-10-24_213806.png',
    alt: 'Immediate postoperative objective assessment — structured clinician examination reference.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/7JU6qTe4rf4rFuF2bkPc7u/Captura_de_pantalla_2026-10-24_213822.png',
    alt: 'Early protection and symptom management — first clinical sessions and authorized interventions.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/eRyTQrLs6p6MhK181EyMjN/Captura_de_pantalla_2026-10-24_213845.png',
    alt: 'Patellar tendon autograft (BTB) pathway — donor-site considerations and early exercise selection.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/d3WJQoxDZYvCyNHYM7f8yu/Captura_de_pantalla_2026-10-24_213903.png',
    alt: 'Closed-kinetic-chain strength progression — sit-to-stand, partial squat, and leg press.',
  },
];

export const insideCategories = [
  { title: 'Therapeutic Exercises', text: 'Positioning, sets, repetitions, and modifications.' },
  { title: 'Postoperative Precautions', text: 'Mobility, loading, and surgeon-directed restrictions.' },
  { title: 'Functional Progressions', text: 'Strength, gait, balance, and movement control.' },
  { title: 'Clinical Assessment', text: 'Mobility, strength, function, and treatment response.' },
  { title: 'Complex Clinical Cases', text: 'Advanced scenarios for informed decisions.' },
  { title: 'Printable Professional Tools', text: 'Exercise cards, home programs, and assessment forms.' },
];

export const bonuses = [
  {
    number: '01',
    title: '40 Complex Postoperative Knee Cases',
    subtitle: '40 Advanced Postoperative Rehabilitation Cases',
    price: 12.9,
    intro: [
      'Advanced cases across ligament, meniscus, replacement, tendon, cartilage, osteotomy, and fracture surgery — with findings, restrictions, decisions, and follow-up.',
    ],
    finalLine: 'Interpret. Compare. Decide. Reassess.',
    meta: '40 Pages of Clinical Cases',
  },
  {
    number: '02',
    title: '40 Knee Exercise Modification Cards',
    subtitle: '40 Professional Cards for Adapting Rehabilitation Exercises',
    price: 10.9,
    intro: ['Each card includes:'],
    list: [
      'Objective and positioning',
      'Step-by-step instructions',
      'Individualized dosage fields',
      'Regressions and progressions',
      'Procedure-specific modifications',
      'Precautions and monitoring',
    ],
    finalLine: 'Select. Perform. Modify. Progress.',
    meta: '40 Pages of Professional Exercise Cards',
  },
  {
    number: '03',
    title: 'Return-to-Sport & Functional Testing Pack',
    subtitle: '40 Functional Assessment and Return-to-Activity Tools',
    price: 15.9,
    intro: ['Tools to evaluate functional recovery after knee surgery:'],
    list: [
      'Strength and range of motion',
      'Functional symmetry',
      'Gait and balance',
      'Hop testing when authorized',
      'Running readiness',
      'Sports and occupational demands',
    ],
    outro: ['Supports professional decisions — not automatic clearance for activity.'],
    finalLine: 'Measure. Record. Interpret. Reassess.',
    meta: '40 Pages of Functional Assessment Tools',
  },
  {
    number: '04',
    title: 'Postoperative Patient Home Program Library',
    subtitle: '40 Customizable Postoperative Home Rehabilitation Handouts',
    price: 9.9,
    intro: ['Patient-facing materials you can customize after knee surgery:'],
    list: [
      'Authorized home exercises',
      'Assistive device guidance',
      'Daily activity instructions',
      'Personalized precautions',
      'Symptom and exercise tracking',
      'Follow-up preparation',
    ],
    finalLine: 'Customize. Explain. Share. Support.',
    meta: '40 Pages of Home Rehabilitation Materials',
  },
  {
    number: '05',
    title: "When Recovery Doesn't Go as Planned",
    subtitle: '40 Clinical Decision Pathways for Unexpected Recovery Challenges',
    price: 12.9,
    intro: ['For when rehabilitation is not progressing as expected:'],
    list: [
      'Persistent loss of mobility',
      'Poor quadriceps activation',
      'Recurrent swelling',
      'Pain during progression',
      'Gait abnormalities',
      'Functional plateaus',
    ],
    outro: ['What to verify, what to consider, and when to contact the surgical team.'],
    finalLine: 'Identify. Analyze. Modify. Refer when necessary.',
    meta: '40 Pages of Clinical Decision Pathways',
  },
];

export const benefits = [
  { title: 'Advanced clinical content', text: 'Procedure-specific situations — not elementary explanations.' },
  { title: 'Step-by-step exercises', text: 'Positioning, dosage examples, and surgical-precaution modifications.' },
  { title: 'Seven surgical areas', text: 'From ligament reconstruction to arthroplasty and fractures.' },
  { title: 'Functional progressions', text: 'Strength, mobility, gait, and function based on recovery.' },
  { title: 'Clinical assessment resources', text: 'Measure progress and organize decisions.' },
  { title: 'Patient education materials', text: 'Customizable home rehabilitation handouts.' },
  { title: 'Printable and reusable', text: 'Print what you need, subject to the license terms.' },
  { title: 'One organized collection', text: 'Volumes, cases, cards, and assessments in one system.' },
];

export const offerItems = [
  '7 postoperative rehabilitation volumes',
  '5 FREE professional bonuses',
  'Resources organized by surgical procedure',
  'Therapeutic exercises and functional progressions',
  '200 cases, cards, tests, handouts & decision pathways',
];

export const audiences = [
  { title: 'Orthopedic physical therapists', text: 'Protocols for patients recovering from different knee surgeries.' },
  { title: 'Sports physical therapists', text: 'Strength, neuromuscular control, and functional preparation.' },
  { title: 'Post-surgical rehab specialists', text: 'Exercises, precautions, and progressions by surgery performed.' },
  { title: 'Physical therapy clinics', text: 'An organized knee rehabilitation library for the whole team.' },
  { title: 'Multidisciplinary teams', text: "Coordinated care alongside the surgeon's instructions." },
];

export const testimonials = [
  {
    name: 'Megan R.',
    age: 34,
    role: 'Sports physical therapist · Denver, USA',
    quote:
      'Most of my caseload is ACL reconstructions in athletes. Having graft-specific considerations next to the exercise progressions saves me real time before every session.',
  },
  {
    name: 'David K.',
    age: 47,
    role: 'Orthopedic physical therapist · Manchester, UK',
    quote:
      'I see a lot of knee replacements. The home program handouts are what I use most — I customize them and patients actually understand what to do between visits.',
  },
  {
    name: 'Lucía F.',
    age: 39,
    role: 'Post-surgical rehabilitation specialist · Valencia, Spain',
    quote:
      "Meniscal root repairs used to mean digging through several protocols. Now I open the right volume and adapt it to the surgeon's orders.",
  },
];

export const faqs = [
  { q: 'Is this a physical product?', a: 'No. It is a 100% digital PDF collection. Nothing will be shipped.' },
  {
    q: 'Who is this designed for?',
    a: 'Orthopedic and sports physical therapists and rehabilitation professionals working with patients after knee surgery.',
  },
  { q: 'Is this a course or certification?', a: 'No. It is a collection of professional clinical references and resources.' },
  {
    q: 'Is it suitable for beginners?',
    a: 'It is intended for professionals familiar with musculoskeletal rehabilitation, therapeutic exercise, and clinical assessment.',
  },
  {
    q: 'Does it include exercises?',
    a: 'Yes — instructions, positioning, progressions, regressions, and dosage considerations by surgery.',
  },
  {
    q: 'Which knee surgeries does it cover?',
    a: 'ACL, meniscus, PCL and multiligament, knee replacement, patellar stabilization, tendon repair, cartilage restoration, osteotomy, and repaired fractures.',
  },
  {
    q: 'What do the five bonuses include?',
    a: 'Complex cases, exercise cards, functional testing, home-program materials, and decision pathways.',
  },
  {
    q: 'Can I print the materials?',
    a: 'Yes, subject to the license terms. Patient materials should be reviewed before distribution.',
  },
  {
    q: "Does it replace the surgeon's instructions?",
    a: 'No. It complements clinical assessment and surgical instructions — it does not replace medical decisions.',
  },
  {
    q: 'How will I receive my purchase?',
    a: "You'll receive access instructions right after payment. Support: compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Jessica Morgan', place: 'Austin, United States', minutes: 3 },
  { name: 'Lukas Weber', place: 'Munich, Germany', minutes: 7 },
  { name: 'Camila Rojas', place: 'Santiago, Chile', minutes: 5 },
  { name: "Hannah O'Brien", place: 'Dublin, Ireland', minutes: 12 },
  { name: 'Matteo Ricci', place: 'Turin, Italy', minutes: 4 },
  { name: 'Aisha Rahman', place: 'Toronto, Canada', minutes: 9 },
  { name: 'Tom Nguyen', place: 'Sydney, Australia', minutes: 14 },
  { name: 'Sofía Herrera', place: 'Mexico City, Mexico', minutes: 6 },
  { name: 'Erik Johansson', place: 'Stockholm, Sweden', minutes: 11 },
  { name: 'Yuki Tanaka', place: 'Tokyo, Japan', minutes: 8 },
];
